import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { useCreateOrderMutation } from "features/api/newOrderApi";
import { useDeleteDraftOrderMutation } from "features/api/orderApi";
import { RootState } from "stores/application.store";
import { newOrderActions } from "features/reducers/newOrder/newOrder";
import { removeImportErrorsFromOrder } from "pages/OrderPages/utils/orderListUtil";
import { getNewOrderURL } from "utils/string";
import { PostOrdersRequestBody } from "types/api/orders/postOrders";
import { useDialog } from "hooks/useDialog.ts";

export const useSubmitOrder = (isAddOn: boolean, isCustomColor: boolean, isNewQuote: boolean) => {
	const [showSkeleton, setShowSkeleton] = useState(false);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const params = useParams();
	const [submitOrder, { error, isLoading, isSuccess }] = useCreateOrderMutation();
	const [deleteDraftOrder] = useDeleteDraftOrderMutation();
	const newOrderDetails: PostOrdersRequestBody | undefined = useSelector(
		(state: RootState) => state.newOrder.newOrderDetails
	);
	const { closeDialog, setDialogAPIInfo, dialogAPIInfo } = useDialog();
	const draftOrder = useSelector((state: RootState) => state.newOrder.draftDetails?.draftOrder);
	const draftId = draftOrder?.draftOrderId;

	useEffect(() => {
		if (dialogAPIInfo?.apiError !== error || dialogAPIInfo?.isLoadingAPI !== isLoading) {
			setDialogAPIInfo({
				apiError: error,
				isLoadingAPI: isLoading
			});
		} else if (isSuccess) {
			setDialogAPIInfo(undefined);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error, isLoading, isSuccess]);

	const handleSubmitOrder = async (orderProcessFee = false, orderOverride?: PostOrdersRequestBody) => {
		closeDialog(false);
		setShowSkeleton(true);
		const baseOrder = orderOverride ?? newOrderDetails;
		const extractedDesignerNumber = baseOrder?.designerNumber?.includes(" - ")
			? baseOrder?.designerNumber.split(" - ")[1]
			: baseOrder?.designerNumber;
		const newOrderDetailsWithExtractedDesignerNumber: PostOrdersRequestBody | undefined = {
			...baseOrder,
			orderProcessFee: orderProcessFee,
			designerNumber: extractedDesignerNumber
		};
		dispatch(newOrderActions.overwriteOrderDetails(newOrderDetailsWithExtractedDesignerNumber));

		await submitOrder(removeImportErrorsFromOrder(newOrderDetailsWithExtractedDesignerNumber))
			.unwrap()
			.then((response) => {
				let confirmationNumber: string | null = null;
				if (typeof response === "string") {
					confirmationNumber = response;
				} else if (typeof response?.data === "string") {
					confirmationNumber = response.data;
				}
				if (confirmationNumber && newOrderDetailsWithExtractedDesignerNumber) {
					dispatch(
						newOrderActions.overwriteOrderDetails({
							...newOrderDetailsWithExtractedDesignerNumber,
							confirmationNumber
						})
					);
				}
				navigate(getNewOrderURL(isAddOn, isCustomColor, isNewQuote, "confirmation", params.orderId));
				if (draftId && draftOrder?.type !== "Template") {
					deleteDraftOrder(draftId ?? "");
				}
				dispatch(newOrderActions.clearDraftError());
			})
			.catch(() => {
				setShowSkeleton(false);
				window.scrollTo(0, 0);
			});
	};

	return { handleSubmitOrder, showSkeleton, setShowSkeleton, error, isLoading };
};
